import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BookOpen, Video, Award, Users, BarChart, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  React.useEffect(() => {
    if (user) {
      if (user.role === 'student') navigate('/student/dashboard');
      else if (user.role === 'teacher') navigate('/teacher/dashboard');
      else if (user.role === 'admin') navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  const features = [
    { icon: Video, title: 'Live Classes', desc: 'Interactive video sessions with real-time chat' },
    { icon: MessageCircle, title: 'Doubt Solving', desc: 'Ask questions anytime, get expert answers' },
    { icon: BookOpen, title: 'Recorded Lectures', desc: 'Access library of pre-recorded content' },
    { icon: Award, title: 'Tests & Quizzes', desc: 'Practice with automated assessments' },
    { icon: BarChart, title: 'Performance Analytics', desc: 'Track progress with detailed reports' },
    { icon: Users, title: 'Course Management', desc: 'Complete learning management system' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <BookOpen className="w-8 h-8 text-indigo-600" />
              <span className="text-2xl font-extrabold text-slate-900">EduFlow</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                data-testid="login-btn"
                variant="ghost"
                onClick={() => navigate('/login')}
                className="font-medium"
              >
                Login
              </Button>
              <Button
                data-testid="register-btn"
                onClick={() => navigate('/register')}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 shadow-lg shadow-indigo-500/20 hover:-translate-y-0.5 transition-all duration-200"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-slide-in">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
                Transform Learning with
                <span className="text-indigo-600"> Professional Coaching</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                Complete online learning platform with live classes, doubt solving, tests, and performance analytics. Built for students, teachers, and educational institutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="hero-get-started-btn"
                  onClick={() => navigate('/register')}
                  className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base font-semibold shadow-lg shadow-indigo-500/20 hover:-translate-y-0.5 transition-all duration-200"
                >
                  Start Learning
                </Button>
                <Button
                  data-testid="explore-courses-btn"
                  onClick={() => navigate('/courses')}
                  variant="outline"
                  className="rounded-full px-8 py-6 text-base font-medium bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
                >
                  Explore Courses
                </Button>
              </div>
            </div>
            <div className="relative animate-slide-in">
              <img
                src="https://images.pexels.com/photos/35539427/pexels-photo-35539427.jpeg"
                alt="Student learning"
                className="rounded-xl shadow-2xl shadow-indigo-500/10 w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Everything You Need to Excel
            </h2>
            <p className="text-lg text-slate-600">
              Comprehensive features for complete learning experience
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-card-${index}`}
                className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 p-6 group card-hover cursor-pointer hover:border-indigo-200"
              >
                <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center mb-4 group-hover:bg-indigo-600 transition-colors duration-300">
                  <feature.icon className="w-6 h-6 text-indigo-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-600 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
            Ready to Start Your Learning Journey?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join thousands of students and teachers already using EduFlow
          </p>
          <Button
            data-testid="cta-get-started-btn"
            onClick={() => navigate('/register')}
            className="rounded-full bg-white hover:bg-slate-50 text-indigo-600 px-8 py-6 text-base font-semibold shadow-lg hover:-translate-y-0.5 transition-all duration-200"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <BookOpen className="w-6 h-6 text-indigo-400" />
            <span className="text-xl font-bold text-white">EduFlow</span>
          </div>
          <p className="text-sm">
            © 2024 EduFlow. Professional coaching & online learning platform.
          </p>
        </div>
      </footer>
    </div>
  );
}
